import VideoControls from './VideoControls';
import Spinner from './Spinner';
import { remoteVideoActive, remoteVideoStreaming } from '../../actions';
import { styledWithProps } from '../../util/styles';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import colors from '../colors';
import * as AlertIcon from 'react-icons/lib/io/alert-circled';

interface Props {
    shouldShow: boolean;
    remoteActive: boolean;
    localActive: boolean;
    remoteStreaming: boolean;
    remoteClosed: boolean;
    connectionError: boolean;
    audioMuted: boolean;
    videoMuted: boolean;
    wrapperComponent: new () => React.Component<any, any>;
    startVideo: (primaryNode: HTMLVideoElement, secondaryNode: HTMLVideoElement) => void;
    closeVideoPanel: () => void;
    endVideo: () => void;
    toggleAudioStream: () => void;
    toggleVideoStream: () => void;
}
interface State {}

class Video extends React.Component<Props, State> {
    primaryVideoEl: HTMLVideoElement | null;
    secondaryVideoEl: HTMLVideoElement | null;

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.shouldShow === false &&
            this.props.shouldShow === true &&
            this.primaryVideoEl &&
            this.secondaryVideoEl
        ) {
            this.props.startVideo(this.primaryVideoEl, this.secondaryVideoEl);
        }
    }

    close = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.closeVideoPanel();
    };

    end = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
        this.props.endVideo();
    };

    renderStatus() {
        const { localActive, remoteStreaming, remoteClosed, connectionError } = this.props;

        if (remoteClosed && !connectionError) {
            return (
                <VideoStatus>
                    <div>The video has ended.</div>
                    <CloseButton href="#" onClick={this.close}>
                        Close
                    </CloseButton>
                </VideoStatus>
            );
        }

        if (connectionError) {
            return (
                <VideoStatus>
                    <ErrorWrapper>
                        <StyledAlertIcon />Oh no! Unable to connect video.
                    </ErrorWrapper>
                    <CloseButton href="#" onClick={this.close}>
                        Close
                    </CloseButton>
                </VideoStatus>
            );
        }

        if (localActive && !remoteStreaming && !connectionError) {
            return (
                <CancelWrapper>
                    <VideoStatusBar>
                        <Spinner />Connecting video. You look nice today!
                    </VideoStatusBar>
                    <CancelButton href="#" onClick={this.end}>
                        Cancel
                    </CancelButton>
                </CancelWrapper>
            );
        }

        return null;
    }

    render() {
        const {
            localActive,
            remoteActive,
            remoteStreaming,
            shouldShow,
            endVideo,
            toggleAudioStream,
            toggleVideoStream,
            videoMuted,
            audioMuted,
        } = this.props;

        const showPrimary = localActive || remoteActive;
        const showSecondary = remoteActive;
        const showControls = localActive && remoteActive;

        if (!shouldShow) return null;

        const Wrapper = this.props.wrapperComponent;

        return (
            <Wrapper>
                {this.renderStatus()}
                {showControls && (
                    <VideoControls
                        videoMuted={videoMuted}
                        audioMuted={audioMuted}
                        end={endVideo}
                        toggleAudio={toggleAudioStream}
                        toggleVideo={toggleVideoStream}
                    />
                )}
                <SecondaryVideo
                    shouldShow={showSecondary}
                    innerRef={(c: HTMLVideoElement) => (this.secondaryVideoEl = c)}
                />

                <PrimaryVideo
                    shouldShow={showPrimary}
                    innerRef={(c: HTMLVideoElement) => (this.primaryVideoEl = c)}
                />
            </Wrapper>
        );
    }
}

export default Video;

const PrimaryVideo = styledWithProps<{ shouldShow: boolean }>(styled.video)`
    height: 100%;
    width: 100%;
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    display: ${(props: any) => (props.shouldShow ? 'block' : 'none')};
`;

const SecondaryVideo = styledWithProps<{ shouldShow: boolean }>(styled.video)`
    height: 30%;
    width: auto;
    background-color: #242424;
    border: 1px solid #242424;
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 1;
    display: ${(props: any) => (props.shouldShow ? 'block' : 'none')};
`;

const VideoStatus = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white;
    z-index: 2;
    padding: 1em;
`;

const VideoStatusBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 10px;
    background-color: ${transparentize(0.1, colors.mediumGray)};
    font-size: 12px;
    color: ${colors.black};
    z-index: 2;
    font-weight: 400;
    text-align: center;
    & svg {
        margin: 0 0.5em 3px 0;
    }
`;

const CloseButton = styled.a`
    display: block;
    background-color: ${colors.white};
    color: ${colors.black};
    border: 0;
    border-radius: 3px;
    padding: 5px 10px;
    margin-top: 1em;
    z-index: 2;
    text-decoration: none;
    transition: all 0.2s;
    &:hover {
        transform: scale(1.1);
    }
`;

const CancelWrapper = styled.div`
    align-self: flex-end;
    z-index: 2;
`;

const CancelButton = styled(CloseButton)`
    align-self: flex-end;
    z-index: 2;
    margin: 0 0 1em 0;
`;

const ErrorWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledAlertIcon = styled(AlertIcon)`
    margin-right: 5px;
    font-size: 20px;
    line-height: 1;
    color: ${colors.yellow};
`;



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/Shared/Video.tsx